import React, { useCallback, useMemo, useState } from "react"
import { Select } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileCsv } from "@fortawesome/free-solid-svg-icons"

import { useProjectsQuery } from "features/Projects/api/useProjectsQuery"
import { PROJECT_FILTER, PROJECT_TYPE } from "types/project.types"
import { AuditChange } from "types/questionnaire.types"
import { exportToCsv } from "utils/exportToCsv"
import { useCompareQuery } from "../api/useProjectVersion"
import { ProjectRef } from "../api/useProjectOwner"
import { collapseRemaps, withoutCodes } from "../utils/historyLabels"
import { ChangeRow } from "./ChangeRow.component"

const CmpRow: React.FC<{ label: string; same: boolean; neutral?: boolean }> = ({ label, same, neutral }) => (
  <div className="flex items-center justify-between text-[12px]">
    <span className="text-slate-600">{label}</span>
    {same ? (
      <span className="font-medium text-emerald-600">● same</span>
    ) : neutral ? (
      <span className="text-slate-400">changed (expected)</span>
    ) : (
      <span className="font-medium text-amber-600">● changed</span>
    )}
  </div>
)

// A change list as its own card panel (header + bounded-scroll body), shown side-by-side so the
// questionnaire and datamap diffs each get a column instead of stacking. Rows and count leave out
// the code changes beside an added, removed or moved answer (collapseRemaps).
// Why a comparison listed nothing, or null when it ran ("ok", or a server that sends no status)
const notCompared = (status: string | undefined): string | null => {
  if (!status || status === "ok") return null
  if (status === "no_qnr") return "One of the projects has no questionnaire, so the questions weren’t compared."
  if (status === "no_data") return "One of the projects has no datamap, so the datamaps weren’t compared."
  return "Couldn’t compare them."
}

const ChangesPanel: React.FC<{
  title: string
  changes: AuditChange[] | null
  emptyHint: string
  status?: string
}> = ({ title, changes, emptyHint, status }) => {
  const reason = notCompared(status)
  const rows = changes && !reason ? collapseRemaps(changes) : null
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200">
      <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {title}
        {rows ? ` (${rows.length})` : ""}
      </div>
      <div className="max-h-[460px] min-h-[72px] overflow-auto">
        {reason ? (
          <div className="px-3 py-3 text-[12px] italic text-slate-400">{reason}</div>
        ) : rows && rows.length > 0 ? (
          <ul className="px-2">
            {rows.map((c, i) => (
              <ChangeRow key={i} change={c} />
            ))}
          </ul>
        ) : rows ? (
          <div className="px-3 py-3 text-[12px] italic text-slate-400">No question or answer changes. {emptyHint}</div>
        ) : (
          <div className="px-3 py-3 text-[12px] text-slate-400">No changes.</div>
        )}
      </div>
    </div>
  )
}

interface WaveCompareProps {
  projectId: string
}

/**
 * Provenance ▸ Compare. Pick another survey project (wave) and diff the full project-version stamp
 * (all four components) for a one-glance "comparable / not" verdict, with the qnr + datamap field
 * drill-downs inline. The superset of the old qnr-only Tracker Compare.
 */
const WaveCompare: React.FC<WaveCompareProps> = ({ projectId }) => {
  // Other survey projects (waves) to compare against.
  const { data: projectsData } = useProjectsQuery(PROJECT_FILTER.BOTH)
  const projects = useMemo(() => projectsData ?? [], [projectsData])

  const [otherId, setOtherId] = useState<string | undefined>(undefined)
  const other = useMemo((): ProjectRef | undefined => {
    const p = otherId ? projects.find((x) => x.id === otherId) : undefined
    return p ? { id: p.id, owner: p.owner } : undefined
  }, [otherId, projects])
  const comparison = useCompareQuery(other, projectId)
  const qnrChanges = comparison.data?.qnrChanges ?? null
  const datamapChanges = comparison.data?.datamapChanges ?? null

  const options = useMemo(
    () =>
      projects
        .filter(
          (p) => p.id !== projectId && (p.metadata?.Type || "").toLowerCase() === PROJECT_TYPE.SURVEY
        )
        .map((p) => ({ value: p.id, label: p.metadata?.Name || p.name || p.id })),
    [projects, projectId]
  )

  const cmp = useMemo(() => {
    if (!comparison.data) return null
    const { version: pv, otherVersion: otherPv } = comparison.data
    // Keys are absent on empty projects — absent keys never match anything.
    const comparable =
      !!pv.comparabilityKey && pv.comparabilityKey === otherPv.comparabilityKey
    // Wording-blind verdict: identical structural keys with different comparability keys means
    // the ONLY difference is question text. Falls back to the 2-state verdict when either side
    // predates the structural key.
    const structural =
      !comparable && !!pv.structuralKey && !!otherPv.structuralKey
        ? pv.structuralKey === otherPv.structuralKey
        : false
    return {
      comparable,
      structural,
      qnr: pv.components.qnr === otherPv.components.qnr,
      datamap: pv.components.datamap === otherPv.components.datamap,
      queryDefs: pv.components.queryDefs === otherPv.components.queryDefs,
      data: pv.components.data === otherPv.components.data,
    }
  }, [comparison.data])

  // Export the field-level differences (questionnaire + datamap) as a flat CSV, never with an
  // answer code.
  const hasChanges =
    (!!qnrChanges && qnrChanges.length > 0) || (!!datamapChanges && datamapChanges.length > 0)
  const handleExportDiff = useCallback(() => {
    const rows: Record<string, string>[] = []
    for (const c of (qnrChanges ?? []).map(withoutCodes))
      rows.push({ component: "questionnaire", change: c.kind, label: c.label, path: c.path, oldValue: c.oldValue ?? "", newValue: c.newValue ?? "" })
    for (const c of (datamapChanges ?? []).map(withoutCodes))
      rows.push({ component: "datamap", change: c.kind, label: c.label, path: c.path, oldValue: c.oldValue ?? "", newValue: c.newValue ?? "" })
    if (rows.length === 0) return
    const wave = otherId ? projects.find((p) => p.id === otherId) : undefined
    const otherName = (wave?.metadata?.Name || wave?.name || "wave").replace(/[^\w-]+/g, "_")
    const date = new Date().toISOString().split("T")[0]
    exportToCsv(rows, `wave-differences-vs-${otherName}-${date}`, {
      columns: ["component", "change", "label", "path", "oldValue", "newValue"],
      headers: ["Component", "Change", "Label", "Path", "Old value", "New value"],
    })
  }, [qnrChanges, datamapChanges, otherId, projects])

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-6">
      {/* picker + verdict + component badges */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Compare to another wave
          </span>
          <Select
            showSearch
            allowClear
            className="min-w-[280px] max-w-md flex-1"
            placeholder="Select a project / wave…"
            optionFilterProp="label"
            options={options}
            value={otherId}
            onChange={(v?: string) => setOtherId(v ?? undefined)}
            notFoundContent="No other survey projects"
          />
        </div>
        {comparison.isLoading ? (
          <div className="mt-3 text-[12px] text-slate-400">Comparing…</div>
        ) : comparison.isError ? (
          <p className="mt-3 text-[12px] font-medium text-rose-600">
            Couldn’t compare with this wave.{" "}
            <button type="button" onClick={() => void comparison.refetch()} className="underline">
              Try again
            </button>
          </p>
        ) : cmp ? (
          <div className="mt-4">
            {cmp.comparable ? (
              <div
                role="status"
                className="rounded-md bg-emerald-50 px-3 py-2 text-[12px] font-medium text-emerald-700"
              >
                ✓ Directly comparable: the definitions are the same, so any difference is in the data.
              </div>
            ) : cmp.structural ? (
              <div
                role="status"
                className="rounded-md bg-sky-50 px-3 py-2 text-[12px] font-medium text-sky-800"
              >
                ≈ Comparable in structure: the same variables, types and answers; only the question
                wording differs.
              </div>
            ) : (
              <div
                role="status"
                className="rounded-md bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-800"
              >
                ✗ Not directly comparable: the definitions differ.
              </div>
            )}
            <div className="mt-3 grid grid-cols-1 gap-x-10 gap-y-1.5 sm:grid-cols-2">
              <CmpRow label="Questionnaire" same={cmp.qnr} />
              <CmpRow label="Datamap" same={cmp.datamap} />
              <CmpRow label="Saved queries" same={cmp.queryDefs} />
              <CmpRow label="Data" same={cmp.data} neutral />
            </div>
            {hasChanges && (
              <button
                type="button"
                onClick={handleExportDiff}
                className="dq-btn mt-3"
              >
                <FontAwesomeIcon icon={faFileCsv} />
                Export differences (CSV)
              </button>
            )}
          </div>
        ) : (
          <p className="mt-3 text-[12px] text-slate-400">
            Pick another survey project to check whether its numbers are defined identically to this one.
          </p>
        )}
      </div>

      {/* field-level diffs, one column each */}
      {cmp && (!cmp.qnr || !cmp.datamap) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {!cmp.qnr && (
            <ChangesPanel
              title="Questionnaire changes"
              changes={qnrChanges}
              status={comparison.data?.qnrStatus}
              emptyHint="The difference is in wording or order only."
            />
          )}
          {!cmp.datamap && (
            <ChangesPanel
              title="Datamap changes"
              changes={datamapChanges}
              status={comparison.data?.datamapStatus}
              emptyHint="The difference is in order only."
            />
          )}
        </div>
      )}

    </div>
  )
}

export default WaveCompare
